import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@hokm/shared';
import type { Scene } from 'phaser';

/**
 * Detect if the current device is iOS (iPhone, iPad, iPod).
 * Also detects iPadOS 13+ which reports as MacIntel with touch support.
 */
export function isIOS(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

// Auto-detect render scale based on screen size and device pixel ratio
// Base game is 1280x720, we scale up to fill the screen crisply
export function getResolutionScale(): number {
  const dpr = window.devicePixelRatio || 1;
  const screenWidth = window.screen.width * dpr;
  const screenHeight = window.screen.height * dpr;

  // Calculate scale needed to fill screen at native resolution
  const scaleX = screenWidth / 1280;
  const scaleY = screenHeight / 720;
  const screenScale = Math.min(scaleX, scaleY);

  // Use the larger of DPR or screen-based scale, clamped to 1-3
  // Note: iOS WebGL stability is handled via crash recovery (falls back to Canvas)
  // and other optimizations (no antialiasing, low-power GPU) - not resolution capping
  const scale = Math.max(dpr, screenScale);
  return Math.min(3, Math.max(1, scale));
}

export const RENDER_SCALE = getResolutionScale();

// Scaled dimensions for rendering
export const SCALED_WIDTH = GAME_WIDTH * RENDER_SCALE;
export const SCALED_HEIGHT = GAME_HEIGHT * RENDER_SCALE;

/**
 * Calculate the zoom level needed to fit the 16:9 game area in the current viewport.
 */
function calculateViewportZoom(scene: Scene): number {
  const viewportWidth = scene.scale.width;
  const viewportHeight = scene.scale.height;

  // Calculate scale to fit 16:9 game area within viewport
  const scaleX = viewportWidth / GAME_WIDTH;
  const scaleY = viewportHeight / GAME_HEIGHT;

  // Use the smaller scale to ensure the entire game area fits
  return Math.min(scaleX, scaleY);
}

/**
 * Call this in scene create() to maintain logical coordinates at 1280x720.
 * With RESIZE mode, this adjusts the camera to center the 16:9 game area
 * within whatever viewport size is available.
 */
export function applyRenderScale(scene: Scene): void {
  const cam = scene.cameras.main;
  const zoom = calculateViewportZoom(scene);
  cam.setZoom(zoom);
  // Center camera on the game world so (0,0) to (GAME_WIDTH, GAME_HEIGHT) is visible
  cam.centerOn(GAME_WIDTH / 2, GAME_HEIGHT / 2);

  // Listen for resize events to update zoom
  scene.scale.on('resize', (gameSize: Phaser.Structs.Size) => {
    const newZoom = Math.min(gameSize.width / GAME_WIDTH, gameSize.height / GAME_HEIGHT);
    cam.setZoom(newZoom);
    cam.centerOn(GAME_WIDTH / 2, GAME_HEIGHT / 2);
  });
}

// Patch Phaser's text factory to automatically set high resolution on all text objects
// Call this once before creating the game
export function patchTextResolution(): void {
  const originalText = Phaser.GameObjects.GameObjectFactory.prototype.text;
  Phaser.GameObjects.GameObjectFactory.prototype.text = function (
    x: number,
    y: number,
    text: string | string[],
    style?: Phaser.Types.GameObjects.Text.TextStyle
  ) {
    const textObj = originalText.call(this, x, y, text, style);
    textObj.setResolution(RENDER_SCALE);
    return textObj;
  };
}
